import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import {
    EditModeStorageSaturationAction,
    EditModeStorageSaturationModalComponent,
} from '@connect-services/client/components/modals/edit-mode-storage-saturation-modal/edit-mode-storage-saturation-modal.component';
import { EditModeConfirmationService } from '@connect-services/client/services/edit-mode-confirmation.service';
import {
    EDIT_SESSION_BY_PROJECT,
    EditSessionByProject,
} from '@connect-services/client/services/project-edit-mode.service';

export type EditModeStorageSaturationUsage = {
    estimatedQuotaBytes: number;
    percentUsed: number;
    usedBytes: number;
};

type OpenStorageSaturationModalParams = {
    canClearHistory: boolean;
    onClearHistory?: () => void;
    storageUsage: EditModeStorageSaturationUsage;
};

@Injectable({
    providedIn: 'root',
})
export class EditModeStorageSaturationModalService {
    private readonly dialog = inject(MatDialog);
    private dialogRef: MatDialogRef<EditModeStorageSaturationModalComponent, EditModeStorageSaturationAction> | null =
        null;
    private readonly editModeConfirmationService = inject(EditModeConfirmationService);

    public openModal(params: OpenStorageSaturationModalParams): void {
        if (this.dialogRef) {
            return;
        }

        this.dialogRef = this.dialog.open(EditModeStorageSaturationModalComponent, {
            autoFocus: false,
            data: {
                canClearHistory: params.canClearHistory,
                hasMultipleEditSessions: this.hasMultipleEditSessions(),
                storageUsage: params.storageUsage,
            },
            disableClose: true,
            restoreFocus: false,
            width: '36rem',
        });

        this.dialogRef.afterClosed().subscribe((action: EditModeStorageSaturationAction | undefined) => {
            this.dialogRef = null;

            if (action === EditModeStorageSaturationAction.CLEAR_HISTORY) {
                params.onClearHistory?.();

                return;
            }

            if (action === EditModeStorageSaturationAction.SAVE) {
                this.editModeConfirmationService.openSaveChangesModal({
                    canEndEditSession: true,
                });
            }
        });
    }

    private hasMultipleEditSessions(): boolean {
        const raw = localStorage.getItem(EDIT_SESSION_BY_PROJECT);

        if (!raw) {
            return false;
        }

        const sessionsByProject = JSON.parse(raw) as Record<string, EditSessionByProject>;

        return Object.keys(sessionsByProject).length > 1;
    }
}
